import React from 'react';
import * as mobx from 'mobx';
import { FormattedMessage } from 'react-intl';
import { ICreateOrganizationDTO, IOrganizationDetailsDTO } from '../../../../../api/organizations.dao';
import { SHORT_PLAIN_TEXT_LENGTH } from '../../../../../constants/common.constants';
import { DOMAIN_REG_EXP, NEW_LINE_REG_EXP } from '../../../../../constants/regex.constants';
import Text from '../../../../global/text/text';
import { IUserDTO } from '../../../../../api/user-groups.dao';
import { FIELD_VALIDATION_PRIORITY_ERROR_MESSAGE } from '../../../../../constants/validation-errors';
import { OrganizationLinkViewModel } from './organization-presenter-participant-link/organization-link.viewmodel';
import { TObject } from '../../../../../infrastructure/type-extensions';

const validationFieldsMessages: ICreateOrganizationDTO = {
    name: 'Name',
    organizationTicker: 'Ticker',
    organizationType: 'Type',
    externalOrganizationId: 'External Id',
};

const defaultValues: ICreateOrganizationDTO = {
    name: '',
    organizationTicker: '',
    organizationType: '',
    externalOrganizationId: '',
    chaperones: [],
    priorityInEvent: null
};

class OrganizationFormDataViewModel {
    @mobx.observable
    public name: string;
    @mobx.observable
    public organizationTicker: string;
    @mobx.observable
    public organizationType: string;
    @mobx.observable
    public externalOrganizationId: string;
    @mobx.observable
    public approvedDomains: string;
    @mobx.observable
    public chaperones: IUserDTO[];
    @mobx.observable
    public priorityInEvent: number;
    @mobx.observable
    public formChecked: boolean;
    @mobx.observable
    private validationErrors: Record<string, JSX.Element> = {};
    private serverErrorMessage: string;
    @mobx.observable
    readonly presenterLink?: OrganizationLinkViewModel;
    @mobx.observable
    readonly participantLink?: OrganizationLinkViewModel;


    @mobx.observable
    private isPresenterLinkCopied: boolean = false;
    @mobx.observable
    private isParticipantLinkCopied: boolean = false;

    constructor(organizationDetails: ICreateOrganizationDTO = defaultValues) {
        this.name = organizationDetails.name || defaultValues.name;
        this.organizationTicker = organizationDetails.organizationTicker || defaultValues.organizationTicker;
        this.organizationType = organizationDetails.organizationType || defaultValues.organizationType;
        this.externalOrganizationId = organizationDetails.externalOrganizationId || defaultValues.organizationType;
        this.formChecked = false;
        this.approvedDomains = organizationDetails.approvedDomains ? organizationDetails.approvedDomains.map(ad => ad.domain).join('\n') : '';
        this.chaperones = organizationDetails.chaperones || defaultValues.chaperones;
        this.priorityInEvent = organizationDetails.priorityInEvent || defaultValues.priorityInEvent;

        const presenterLinkVm = OrganizationLinkViewModel.initPresenterLink(organizationDetails as IOrganizationDetailsDTO);
        if (presenterLinkVm) this.presenterLink = presenterLinkVm;

        const participantLinkVm = OrganizationLinkViewModel.initParticipantLink(organizationDetails as IOrganizationDetailsDTO);
        if (participantLinkVm) this.participantLink = participantLinkVm;
    }

    public setFieldsInvalidByApi(fieldsMatch: TObject<string>): void {
        [...Object.entries(fieldsMatch)]
            .forEach(([name, message]) => (Reflect.get(this, name) as OrganizationLinkViewModel)?.setAsInvalidByApi(message))
        ;
    }

    public getVmFieldNames(): string[] {
        return [ this.participantLink?.name, this.presenterLink?.name ].filter(Boolean);
    }

    @mobx.action
    public setChaperones = (users: IUserDTO[]) => {
        mobx.runInAction(() => {
            this.chaperones = users.map(user => {
                return {
                    firstName: user.firstName,
                    lastName: user.lastName,
                    username: user.username
                };
            });
        });
    };
    @mobx.action
    public removeChaperone = (username: string) => {
        this.chaperones = this.chaperones.filter(coordinator => coordinator.username !== username);
    };

    @mobx.action
    public getNameError = () => {
        return this.name
            ? this.getFieldError('name')
            : <FormattedMessage
                id='oe.errors.name.cantBeEmpty'
                description='Organization name label'
                defaultMessage="Name can't be empty"
            />;
    };

    private validateFieldsLength() {
        for (const fields of Object.keys(validationFieldsMessages)) {
            if (this.getFieldError(fields)) {
                return true;
            }
        }
    }

    public getFieldError(field: string) {
        return this[field]?.length > SHORT_PLAIN_TEXT_LENGTH ? `${validationFieldsMessages[field]} is too long` : null;
    }

    public getFieldValidationError(field: string): JSX.Element | undefined {
        return this.validationErrors[field];
    }

    public getPriorityInEventError = (): JSX.Element | null => {
        if (this.priorityInEvent) {
            if (this.priorityInEvent < 1 || this.priorityInEvent > 99) {
                return FIELD_VALIDATION_PRIORITY_ERROR_MESSAGE(1, 99);
            }
        }
        return null;
    };

    @mobx.action
    public getApprovedDomainsError = () => {
        if (this.validationErrors.approvedDomains) {
            delete this.validationErrors.approvedDomains;
        }

        if (this.approvedDomains.length) {
            const domains = this.approvedDomains.split(NEW_LINE_REG_EXP);
            if (domains.length > 50) {
                this.validationErrors.approvedDomains = (
                    <FormattedMessage
                        defaultMessage='You can enter 50 domains as max'
                        description='Error message text'
                        id='oe.organization.errors.maxDomainLimit' />);
            }
            const wrongDomains = domains.filter(d => {
                const domain = d.trim();
                if (domain && (!domain.match(DOMAIN_REG_EXP))) {
                    return true;
                }
            });
            if (wrongDomains.length) {
                this.validationErrors.approvedDomains = wrongDomains.length === 1
                    ? <FormattedMessage
                        defaultMessage='{wrongDomain} is not a valid domain'
                        description='Error message text'
                        id='oe.organization.errors.wrongApprovedDomain'
                        values={{
                            wrongDomain: wrongDomains
                        }}
                    />
                    : <>
                        {wrongDomains.map((wd, i) => <Text tag='p' key={i} message={wd.trim()} withEllipsis />)}
                        <FormattedMessage
                            defaultMessage='are not valid domains'
                            description='Error message text'
                            id='oe.organization.errors.wrongApprovedDomains'
                        />
                    </>;
            }
            const domainsWithoutEmptyStrings = this.approvedDomains.split(NEW_LINE_REG_EXP).filter(ad => ad.trim() && ad);
            const notUniqueDomains = (new Set(domainsWithoutEmptyStrings)).size !== domainsWithoutEmptyStrings.length;
            if (notUniqueDomains) {
                this.validationErrors.approvedDomains = (
                    <FormattedMessage
                        defaultMessage='You have to enter unique domains'
                        description='Error message text'
                        id='oe.organization.errors.notUniqueDomains' />);
            }
        }
    };

    @mobx.action
    public checkForm = () => {
        this.formChecked = true;
        return (
            !this.getNameError()
            && !this.validateFieldsLength()
            && !this.validationErrors.approvedDomains
            && (this.presenterLink ? this.presenterLink.isValid : true)
            && (this.participantLink ? this.participantLink.isValid : true)
            && !this.getPriorityInEventError()
        );
    };

    public toAPIDTO(): ICreateOrganizationDTO {
        const dto = {
            name: this.name?.trim(),
            organizationTicker: this.organizationTicker?.trim(),
            organizationType: this.organizationType?.trim(),
            externalOrganizationId: this.externalOrganizationId?.trim() || null,
            approvedDomains: this.approvedDomains.length
                ? this.approvedDomains.split(NEW_LINE_REG_EXP).filter(ad => !!(ad.trim())).map(ad => { return { domain: ad.trim() }; }) : [],

            chaperones: this.chaperones,
            priorityInEvent: this.priorityInEvent || null
        };

        if (this.presenterLink) dto[this.presenterLink.name] = this.presenterLink.toDTO();
        if (this.participantLink) dto[this.participantLink.name] = this.participantLink.toDTO();

        return dto;
    }

    public getServerErrorMessage(): string {
        return this.serverErrorMessage;
    }

    @mobx.action
    public setServerErrorMessage(message: string): void {
        this.serverErrorMessage = message;
    }


    @mobx.action
    public setIsPresenterLinkCopied(isCopied: boolean): void {
        this.isPresenterLinkCopied = isCopied;
    }

    @mobx.action
    public getIsPresenterLinkCopied(): boolean {
        return this.isPresenterLinkCopied;
    }


    @mobx.action
    public setIsParticipantLinkCopied(isCopied: boolean): void {
        this.isParticipantLinkCopied = isCopied;
    }

    @mobx.action
    public getIsParticipantLinkCopied(): boolean {
        return this.isParticipantLinkCopied;
    }
}

export { OrganizationFormDataViewModel };
